import { Brand } from '../components/ui/Brand';
import { useState, useEffect, useRef } from 'react';
import { getRestaurants, createRestaurant, setRestaurantStatus } from '../utils/api';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { useToast } from '../components/ui/Toast';
import { usePageTitle } from '../hooks/usePageTitle';

export function RestaurantPickerPage({ user, onSelect, onLogout, theme, onToggleTheme }) {
  usePageTitle('Select Location');
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const requestId = useRef(0);
  const [showAdd, setShowAdd] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  const [name, setName] = useState('');
  const [sheetId, setSheetId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const toast = useToast();

  useEffect(() => {
    fetchRestaurants();
    return () => { requestId.current += 1; };
  }, []);

  useEffect(() => {
    if (!loading && !loadError && user.role === 'manager' && restaurants.length === 1 && restaurants[0].active) {
      onSelect(restaurants[0]);
    }
  }, [restaurants, loading, loadError]); // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchRestaurants() {
    const id = ++requestId.current;
    setLoading(true);
    setLoadError(false);
    try {
      const data = await getRestaurants();
      if (id !== requestId.current) return;
      if (!data.ok || !Array.isArray(data.restaurants)) throw new Error('Locations unavailable');
      setRestaurants(data.restaurants);
    } catch {
      if (id !== requestId.current) return;
      setRestaurants([]);
      setLoadError(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }

  async function handleAddRestaurant(e) {
    e.preventDefault();
    setSubmitting(true);
    const data = await createRestaurant({ name, sheetId });
    if (data.ok) {
      toast('Restaurant added!', 'success');
      setShowAdd(false);
      setName(''); setSheetId('');
      fetchRestaurants();
    } else {
      toast(data.message, 'error');
    }
    setSubmitting(false);
  }

  async function handleSetStatus(r, active) {
    const action = active ? 'reactivate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} ${r.name}?`)) return;
    const data = await setRestaurantStatus(r.id, active);
    if (data.ok) {
      toast(data.message, 'success');
      fetchRestaurants();
    } else {
      toast(data.message, 'error');
    }
  }

  const colors = [
    'linear-gradient(135deg, #f59e0b, #d97706)',
    'linear-gradient(135deg, #0ea5e9, #2563eb)',
    'linear-gradient(135deg, #10b981, #059669)',
    'linear-gradient(135deg, #8b5cf6, #6d28d9)',
    'linear-gradient(135deg, #f43f5e, #e11d48)',
  ];

  const activeRestaurants = restaurants.filter(r => r.active);
  const inactiveRestaurants = restaurants.filter(r => !r.active);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)' }}>

      {/* TOP BAR */}
      <div style={{
        background: 'var(--surface)',
        borderBottom: '1px solid var(--border)',
        padding: '14px 24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
      }}>
        <Brand />
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {user.role === 'admin' && (
            <span style={{
              background: 'var(--warning-bg)', color: 'var(--warning)',
              border: '1px solid var(--warning-border)',
              borderRadius: '999px', padding: '4px 10px',
              fontSize: '11px', fontWeight: '700',
            }}>👑 Admin</span>
          )}
          <button
            onClick={onToggleTheme}
            style={{
              background: 'var(--bg-secondary)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', padding: '7px 12px',
              fontSize: '13px', cursor: 'pointer', color: 'var(--text-2)',
            }}
          >
            {theme === 'dark' ? '☀️' : '🌙'}
          </button>
          <Button variant="ghost" size="sm" onClick={onLogout}>Sign Out</Button>
        </div>
      </div>

      {/* CONTENT */}
      <div style={{ maxWidth: '560px', margin: '0 auto', padding: '48px 24px' }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '24px', fontWeight: '800',
            color: 'var(--text)', marginBottom: '6px',
          }}>
            Welcome back, {user.name.split(' ')[0]} 👋
          </h1>
          <p style={{ color: 'var(--text-3)', fontSize: '14px' }}>
            Select a location to manage
          </p>
        </div>

        {/* ACTIVE RESTAURANTS */}
        {loading ? (
          <div role="status" aria-label="Loading locations" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} className="skeleton" style={{ height: '80px', borderRadius: 'var(--radius-lg)' }} />
            ))}
          </div>
        ) : loadError ? (
          <div role="alert" className="card" style={{ padding: '24px' }}>
            <p>Couldn’t load your locations. Please try again.</p>
            <Button onClick={fetchRestaurants}>Retry</Button>
          </div>
        ) : activeRestaurants.length === 0 ? (
          <div className="card" style={{ padding: '24px' }}>
            <p>No active locations available.</p>
            <p style={{ color: 'var(--text-3)' }}>{user.role === 'admin' ? 'Add a location or reactivate an existing one.' : 'Ask your administrator to check your location access.'}</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {activeRestaurants.map((r, i) => (
              <div key={r.id} className="card" style={{ padding: '0', overflow: 'hidden' }}>
                {/* MAIN CLICK AREA */}
                <button
                  onClick={() => onSelect(r)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: '18px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    cursor: 'pointer',
                    textAlign: 'left',
                    width: '100%',
                    transition: 'var(--transition)',
                  }}
                  onMouseOver={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                  onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                >
                  <div style={{
                    width: '48px', height: '48px',
                    borderRadius: '14px',
                    background: colors[i % colors.length],
                    display: 'flex', alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px', flexShrink: 0,
                  }}>
                    🏪
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '15px', fontWeight: '700',
                      color: 'var(--text)',
                    }}>
                      {r.name}
                    </div>
                    <div style={{
                      fontSize: '12px', color: 'var(--text-3)',
                      marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px',
                    }}>
                      <span style={{
                        width: '6px', height: '6px', borderRadius: '50%',
                        background: '#10b981', display: 'inline-block',
                      }} />
                      Active · Click to manage
                    </div>
                  </div>
                  <div style={{ fontSize: '18px', color: 'var(--text-4)' }}>→</div>
                </button>

                {/* ADMIN ACTIONS */}
                {user.role === 'admin' && (
                  <div style={{
                    borderTop: '1px solid var(--border)',
                    padding: '10px 20px',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    background: 'var(--bg-secondary)',
                  }}>
                    <button
                      onClick={() => handleSetStatus(r, false)}
                      style={{
                        background: 'var(--danger-bg)',
                        border: '1px solid var(--danger-border)',
                        color: 'var(--danger)',
                        borderRadius: '8px',
                        padding: '5px 12px',
                        fontSize: '12px',
                        fontWeight: '700',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                      }}
                    >
                      🔴 Deactivate
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* INACTIVE RESTAURANTS — admin only */}
        {!loading && !loadError && user.role === 'admin' && inactiveRestaurants.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <button
              onClick={() => setShowInactive(s => !s)}
              style={{
                background: 'none', border: 'none',
                color: 'var(--text-3)', fontSize: '13px',
                fontWeight: '700', cursor: 'pointer',
                fontFamily: 'inherit', padding: '0',
                display: 'flex', alignItems: 'center', gap: '6px',
              }}
            >
              {showInactive ? '▾' : '▸'} {inactiveRestaurants.length} inactive location{inactiveRestaurants.length > 1 ? 's' : ''}
            </button>

            {showInactive && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginTop: '12px' }}>
                {inactiveRestaurants.map((r, i) => (
                  <div key={r.id} className="card" style={{ padding: '0', overflow: 'hidden', opacity: 0.7 }}>
                    <div style={{
                      padding: '16px 20px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '16px',
                    }}>
                      <div style={{
                        width: '44px', height: '44px',
                        borderRadius: '12px',
                        background: '#94a3b8',
                        display: 'flex', alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '22px', flexShrink: 0,
                        filter: 'grayscale(1)',
                      }}>
                        🏪
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          fontFamily: 'var(--font-display)',
                          fontSize: '15px', fontWeight: '700',
                          color: 'var(--text-3)',
                        }}>
                          {r.name}
                        </div>
                        <div style={{
                          fontSize: '12px', color: 'var(--text-4)',
                          marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px',
                        }}>
                          <span style={{
                            width: '6px', height: '6px', borderRadius: '50%',
                            background: '#dc2626', display: 'inline-block',
                          }} />
                          Inactive
                        </div>
                      </div>
                    </div>
                    <div style={{
                      borderTop: '1px solid var(--border)',
                      padding: '10px 20px',
                      display: 'flex',
                      justifyContent: 'flex-end',
                      background: 'var(--bg-secondary)',
                    }}>
                      <button
                        onClick={() => handleSetStatus(r, true)}
                        style={{
                          background: 'var(--success-bg)',
                          border: '1px solid var(--success-border)',
                          color: 'var(--success)',
                          borderRadius: '8px',
                          padding: '5px 12px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                        }}
                      >
                        🟢 Reactivate
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ADD RESTAURANT — admin only */}
        {user.role === 'admin' && (
          <div style={{ marginTop: '24px', textAlign: 'center' }}>
            <Button variant="ghost" onClick={() => setShowAdd(true)}>
              + Add New Restaurant
            </Button>
          </div>
        )}
      </div>

      {/* ADD RESTAURANT MODAL */}
      <Modal
        open={showAdd}
        onClose={() => setShowAdd(false)}
        title="Add New Restaurant"
        subtitle="Create a new location. Share its Google Sheet with your service account first."
      >
        <form onSubmit={handleAddRestaurant}>
          <div className="field">
            <label className="label">Restaurant Name</label>
            <input
              className="input"
              type="text"
              placeholder="e.g. Scarborough"
              value={name}
              onChange={e => setName(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label className="label">Google Sheet ID</label>
            <input
              className="input"
              type="text"
              placeholder="Paste the Sheet ID from the URL"
              value={sheetId}
              onChange={e => setSheetId(e.target.value)}
              required
            />
          </div>
          <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
            <Button type="submit" variant="primary" full loading={submitting}>
              Add Restaurant
            </Button>
            <Button variant="ghost" onClick={() => setShowAdd(false)}>
              Cancel
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
